import { faCircleInfo, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse, Tooltip } from "antd";
import type React from "react";
import { useState } from "react";
import styled from "styled-components";
import { ROLE_NAME_OPTIONS } from "../../constants/formEnums";
import type { Person } from "../../types/formTypes";
import MultiSelectField from "./MultiSelectField";
import TagInputField from "./TagInputField";
import TextField from "./TextField";

const FieldWrapper = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const LabelRow = styled.label`
  display: inline-flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
  font-weight: 700;
  font-size: 16px;
  cursor: text;
  text-align: left;
`;

const RequiredMark = styled.span`
  color: #d32f2f;
`;

const ContributorList = styled.div`
  margin-top: 6px;
`;

const StyledCollapse = styled(Collapse)`
  .ant-collapse-item {
    margin-bottom: 8px;
  }
  .ant-collapse-header {
    align-items: center !important;
  }
  .ant-collapse-header button {
    width: auto !important;
    min-width: unset !important;
    flex: 0 0 auto !important;
    background: none !important;
    border: none !important;
    padding: 4px 8px !important;
  }
  .ant-collapse-content-box {
    padding: 12px 16px !important;
    width: 100%;
    box-sizing: border-box;
  }
`;

const ContributorFieldsWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;

  input,
  .ant-select {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
`;

const RemoveButton = styled.button`
  flex: 0 0 auto !important;
  background: none !important;
  border: none !important;
  cursor: pointer;
  padding: 4px 8px !important;
  color: var(--color-primary-desat) !important;
  font-size: 0.85rem;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: auto !important;
  min-width: auto !important;
  margin-left: auto;

  &:hover {
    color: #d32f2f !important;
  }
`;

const AddButton = styled.button`
  flex: 0 0 auto !important;
  margin-top: 12px;
  padding: 10px 40px !important;
  font-size: 0.9rem;
  border-radius: 6px;
  border: 1px dashed var(--color-primary-desat) !important;
  background: transparent !important;
  color: var(--color-primary) !important;
  cursor: pointer;

  &:hover {
    border-color: var(--color-primary) !important;
    background: rgba(0, 0, 0, 0.04) !important;
  }
`;

export interface ContributorFieldProps {
  label: string;
  name: string;
  value: Person[];
  onChange: (value: Person[]) => void;
  required?: boolean;
  tooltip?: string;
  /** Label for each item in the list, e.g. "Contributor" or "Author" */
  itemLabel?: string;
  /** Label for the add button, e.g. "+ Add Contributor" or "+ Add Author" */
  addButtonLabel?: string;
}

const emptyPerson: Person = {
  givenName: "",
  familyName: "",
  identifier: "",
  roleName: [],
  email: [],
};

const ContributorField: React.FC<ContributorFieldProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  tooltip,
  itemLabel = "Contributor",
  addButtonLabel = "+ Add Contributor",
}) => {
  const initialItems = value.length > 0 ? value : required ? [emptyPerson] : [];
  const [activeKeys, setActiveKeys] = useState<string[]>(() =>
    initialItems.map((_, i) => String(i)),
  );

  const contributors = value.length > 0 ? value : required ? [emptyPerson] : [];

  const updatePerson = (index: number, updates: Partial<Person>) => {
    const next = [...contributors];
    next[index] = { ...next[index], ...updates };
    onChange(next);
  };

  const addContributor = () => {
    const next = [...contributors, { ...emptyPerson }];
    onChange(next);
    setActiveKeys((prev) => [...prev, String(next.length - 1)]);
  };

  const removeContributor = (index: number) => {
    if (contributors.length <= 1 && required) return;
    const next = contributors.filter((_, i) => i !== index);
    onChange(next);
    setActiveKeys((prev) =>
      prev
        .filter((k) => k !== String(index))
        .map((k) =>
          parseInt(k, 10) > index ? String(parseInt(k, 10) - 1) : k,
        ),
    );
  };

  const items = contributors.map((person, index) => ({
    key: String(index),
    label: (
      <PanelHeader>
        <span>{itemLabel}</span>
        {(contributors.length > 1 || !required) && contributors.length >= 1 && (
          <RemoveButton
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeContributor(index);
            }}
            aria-label={`Remove ${itemLabel.toLowerCase()}`}
          >
            <FontAwesomeIcon icon={faTrash} />
          </RemoveButton>
        )}
      </PanelHeader>
    ),
    children: (
      <ContributorFieldsWrapper>
        <TextField
          label="Given Name"
          name={`${name}-${index}-givenName`}
          value={person.givenName ?? ""}
          onChange={(v) => updatePerson(index, { givenName: v })}
          required
          tooltip="Given name. In the U.S., the first name of a Person. This can be used along with familyName instead of the name property."
        />
        <TextField
          label="Family Name"
          name={`${name}-${index}-familyName`}
          value={person.familyName ?? ""}
          onChange={(v) => updatePerson(index, { familyName: v })}
          required
          tooltip="Family name. In the U.S., the last name of a Person. This can be used along with givenName instead of the name property."
        />
        <TextField
          label="Identifier"
          name={`${name}-${index}-identifier`}
          value={person.identifier ?? ""}
          onChange={(v) => updatePerson(index, { identifier: v })}
          tooltip="URL identifier, ideally an ORCID ID for individuals, a FundRef ID for funders."
        />
        <MultiSelectField
          label="Role"
          name={`${name}-${index}-roleName`}
          value={Array.isArray(person.roleName) ? person.roleName : []}
          onChange={(v) => updatePerson(index, { roleName: v })}
          options={ROLE_NAME_OPTIONS}
          tooltip="Detailed role(s) of a person."
        />
        <TagInputField
          label="E-Mail Address"
          name={`${name}-${index}-email`}
          value={Array.isArray(person.email) ? person.email : []}
          onChange={(v) => updatePerson(index, { email: v })}
          tooltip="Email address(es) of the person."
          placeholder="Type email(s) and press Enter"
        />
      </ContributorFieldsWrapper>
    ),
  }));

  return (
    <FieldWrapper>
      <LabelRow htmlFor={name}>
        {label}
        {required && <RequiredMark>*</RequiredMark>}
        {tooltip && (
          <Tooltip title={tooltip}>
            <span style={{ display: "inline-flex", cursor: "help" }}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{
                  color: "var(--color-primary-desat)",
                  fontSize: "0.9rem",
                }}
              />
            </span>
          </Tooltip>
        )}
      </LabelRow>
      <ContributorList>
        <StyledCollapse
          activeKey={activeKeys}
          onChange={(keys) =>
            setActiveKeys(Array.isArray(keys) ? keys : [keys])
          }
          items={items}
        />
        <AddButton type="button" onClick={addContributor}>
          {addButtonLabel}
        </AddButton>
      </ContributorList>
    </FieldWrapper>
  );
};

export default ContributorField;
